class AofChatbotWidget extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.conversationId = this.createConversationId();

    this.launcher = this.querySelector('[data-chatbot-launcher]');
    this.panel = this.querySelector('[data-chatbot-panel]');
    this.closeButton = this.querySelector('[data-chatbot-close]');
    this.form = this.querySelector('[data-chatbot-form]');
    this.input = this.querySelector('[data-chatbot-input]');
    this.messages = this.querySelector('[data-chatbot-messages]');
    this.defaultPlaceholder = this.input?.getAttribute('placeholder') || '';

    this.launcher?.addEventListener('click', () => this.togglePanel());
    this.closeButton?.addEventListener('click', () => this.closePanel());
    this.form?.addEventListener('submit', (event) => this.handleSubmit(event));

    for (const button of this.querySelectorAll('[data-chatbot-action="search-help"]')) {
      button.addEventListener('click', () => this.showSearchHelp());
    }

    for (const button of this.querySelectorAll('[data-chatbot-suggestion]')) {
      button.addEventListener('click', () => {
        const message = button.dataset.chatbotSuggestion || '';
        if (!message) return;

        this.openPanel();
        if (button.hasAttribute('data-chatbot-submit')) {
          this.sendMessage(message);
          return;
        }

        this.input.value = message;
        this.input.focus();
      });
    }
  }

  createConversationId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `aof-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  togglePanel() {
    if (this.panel?.hidden) {
      this.openPanel();
      return;
    }

    this.closePanel();
  }

  openPanel() {
    const closedDrawer = this.closeBlockingDrawers();
    this.panel.hidden = false;
    this.launcher?.setAttribute('aria-expanded', 'true');
    window.setTimeout(() => {
      if (!this.panel.hidden) this.input?.focus();
    }, closedDrawer ? 260 : 0);
  }

  closeBlockingDrawers() {
    let closedDrawer = false;

    for (const drawer of document.querySelectorAll('header-drawer')) {
      if (drawer === this) continue;
      if (typeof drawer.close === 'function' && drawer.isOpen) {
        drawer.close();
        closedDrawer = true;
      }
    }

    return closedDrawer;
  }

  closePanel() {
    this.panel.hidden = true;
    this.launcher?.setAttribute('aria-expanded', 'false');
    this.launcher?.focus();
  }

  async handleSubmit(event) {
    event.preventDefault();

    const message = this.input.value.trim();
    if (!message) return;

    this.input.value = '';
    await this.sendMessage(message);
  }

  showSearchHelp() {
    this.openPanel();
    this.input.value = '';
    this.input.placeholder = '例: ラベンダー / lavender';
    this.appendMessage('商品名・素材名を入力してください。\nEnter a product or material name.', 'bot');
    this.input.focus();
  }

  async sendMessage(message) {
    if (!message) return;

    this.appendMessage(message, 'user');
    this.setBusy(true);

    try {
      const response = await this.requestReply(message);
      response.originalMessage = message;
      this.appendBotResponse(response);
    } catch (error) {
      this.appendMessage(
        '現在チャットの接続を確認しています。お急ぎの場合はお問い合わせフォームをご利用ください。',
        'bot',
        { error: true }
      );
    } finally {
      this.setBusy(false);
      if (this.defaultPlaceholder) this.input.placeholder = this.defaultPlaceholder;
      this.input.focus();
    }
  }

  async requestReply(message) {
    if (this.dataset.demoMode === 'true') {
      await new Promise((resolve) => window.setTimeout(resolve, 260));
      return this.createDemoReply(message);
    }

    const endpoint = this.dataset.apiEndpoint || '/chat';
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        message,
        conversationId: this.conversationId,
        page: {
          url: window.location.href,
          title: document.title,
          template: this.dataset.template || '',
        },
        product: {
          id: this.dataset.productId || null,
          title: this.dataset.productTitle || null,
          url: this.dataset.productUrl || null,
        },
      }),
    });

    if (!response.ok) throw new Error(`Chat API failed: ${response.status}`);
    return response.json();
  }

  createDemoReply(message) {
    const normalized = message.toLowerCase();
    const contactUrl = this.dataset.contactUrl || '/pages/contact';

    if (/(注文|住所|発送先|配送先|送付先|届け先|お届け先|変更|キャンセル|クレーム|返金|注文番号|問い合わせ|お問い合わせ|連絡|order|address|change|contact)/i.test(message)) {
      return {
        category: 'human_required',
        reply: this.createContactGuidance(message),
        handoff: { label: 'お問い合わせフォームを開く', url: contactUrl },
      };
    }

    if (/(発送|配送|届|送料|shipping|delivery)/i.test(message)) {
      return {
        category: 'faq_shipping',
        reply:
          message.includes('送料') || /shipping fee|delivery fee/i.test(message)
            ? '送料はご注文内容や配送先によって変わるため、チェックアウト画面で計算されます。商品をカートに入れたあと、購入手続き画面で送料をご確認ください。'
            : '在庫のある商品は、通常ご注文確認後10日以内の発送を目安にしています。商品やご注文内容によって確認が必要な場合がありますので、詳しい状況確認が必要な場合はお問い合わせフォームよりご連絡ください。',
      };
    }

    if (/(返品|交換|return|refund)/i.test(message)) {
      return {
        category: 'faq_return',
        reply:
          '商品の性質上、お客様都合による返品・交換は原則として承っておりません。大幅な漏れ、容器破損、誤出荷など重大な欠陥がある場合は、商品到着日を含む3日以内に、商品の状態が分かる画像を添付してお問い合わせください。',
        handoff: { label: '返品・交換を問い合わせる', url: contactUrl },
      };
    }

    if (/(プレゼント|ギフト|gift|初心者|香り|甘|おすすめ|男性|女性|ユニセックス|春|夏|秋|冬|季節|精油|香料|オイル|ラベンダー|探して|recommend)/i.test(message) || normalized.includes('present')) {
      return this.createProductConsultationReply(message);
    }

    return {
      category: 'unknown',
      reply: this.createContactGuidance(message),
      handoff: { label: 'お問い合わせフォームを開く', url: contactUrl },
    };
  }

  createProductConsultationReply(message) {
    const query = encodeURIComponent(
      message
        .replace(/[？?！!。、「」『』（）()［\]【】]/g, ' ')
        .replace(/(ありますか|探しています|探してます|探して|おすすめ|オススメ|商品|香り|精油|香料|オイル|について|ください|です|ます|は|が|を|の|に|で|と)/g, ' ')
        .replace(/\s+/g, ' ')
        .trim() || message
    );

    return {
      category: 'product_consultation',
      reply:
        '商品名や素材名で検索します。候補が多い場合は、検索結果ページでその他の商品もご確認ください。',
      products: [
        {
          title: '商品検索を開く',
          url: `/search?q=${query}&type=product`,
          meta: '検索結果で詳しく確認する',
        },
      ],
      handoff: { label: '検索結果をすべて見る', url: `/search?q=${query}&type=product` },
    };
  }

  createContactGuidance(message) {
    return [
      '個別確認が必要な内容です。お手数ですが、お問い合わせフォームからご連絡ください。',
      '',
      'フォームの「コメント」欄には、下記のように入れていただくと確認がスムーズです。',
      '',
      '【お問い合わせ内容】',
      message || '確認したい内容を入力してください。',
      '',
      '【詳細】',
      'お名前、メールアドレス、電話番号はフォームの各欄にご入力ください。',
    ].join('\n');
  }

  appendBotResponse(response) {
    const text = response.reply || response.message || response.answer || '回答を取得しました。';
    const wrapper = this.appendMessage(text, 'bot', { returnElement: true });

    if (Array.isArray(response.products) && response.products.length > 0) {
      const products = document.createElement('div');
      products.className = 'aof-chatbot__products';

      for (const product of response.products.slice(0, 3)) {
        const link = document.createElement('a');
        link.className = 'aof-chatbot__product-link';
        link.href = product.url || '/collections/all';

        const title = document.createElement('span');
        title.className = 'aof-chatbot__product-title';
        title.textContent = product.title || '商品を見る';
        link.append(title);

        if (product.meta || product.price) {
          const meta = document.createElement('span');
          meta.className = 'aof-chatbot__product-meta';
          meta.textContent = product.meta || product.price;
          link.append(meta);
        }

        products.append(link);
      }

      wrapper.append(products);
    }

    if (response.handoff?.url) {
      const handoff = document.createElement('div');
      handoff.className = 'aof-chatbot__products';
      const link = document.createElement('a');
      link.className = 'aof-chatbot__product-link';
      link.href = response.handoff.url;
      link.textContent = response.handoff.label || 'お問い合わせへ進む';
      handoff.append(link);
      wrapper.append(handoff);
    }

  }

  appendMessage(text, role, options = {}) {
    const message = document.createElement('div');
    message.className = `aof-chatbot__message aof-chatbot__message--${role}`;
    if (options.error) message.classList.add('aof-chatbot__message--error');

    const paragraph = document.createElement('p');
    paragraph.textContent = text;
    message.append(paragraph);
    this.messages.append(message);
    this.messages.scrollTop = this.messages.scrollHeight;

    if (options.returnElement) return message;
    return null;
  }

  setBusy(isBusy) {
    this.input.disabled = isBusy;
    this.form.querySelector('button[type="submit"]').disabled = isBusy;
  }

}

if (!customElements.get('aof-chatbot-widget')) {
  customElements.define('aof-chatbot-widget', AofChatbotWidget);
}
